import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import RouteViewer from './RouteViewer'
import { User, ChevronUp, ChevronDown, Share2, Info, PlusIcon, Camera, DumbbellIcon, TrashIcon, Map as MapIcon, MessageSquare, Square, CheckCircle } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts'
import LanguageSwitcher from './LanguageSwitcher'
import ImageUpload from './ImageUpload'
const ShareModal = React.lazy(() => import('./ShareModal'))
import ExerciseSelector from './ExerciseSelector'
import BodyPartRadarChart from './BodyPartRadarChart'
import Calendar from './Calendar'
import PeriodComparisonChart from './PeriodComparisonChart'
import DailyVolumeChart from './DailyVolumeChart'
import Footer from './Footer'
import ImportWorkout from './ImportWorkout'
import HumanBodyDiagram from './HumanBodyDiagram'
import LiveTracker from './LiveTracker'
import TrialBanner from './TrialBanner'
import WeeklyPlanCard from './WeeklyPlanCard'
import { stripMarkdown } from '../utils/aiText'
import GoalPromptBanner from './GoalPromptBanner'
import LockedFeatureOverlay from './LockedFeatureOverlay'
import PremiumUpgradeModal from './PremiumUpgradeModal'

const Dashboard = ({ onOpenMenu, onNavigate, currentUser }) => {
  const { t, i18n } = useTranslation()
  const [logs, setLogs] = useState([])
  const [exercises, setExercises] = useState([])
  const [loading, setLoading] = useState(true)
  const [showMap, setShowMap] = useState(true); // for LiveTracker
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [shareModalType, setShareModalType] = useState('workout')
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)

  // Membership status
  const isFreeMember = currentUser?.membership_status === 'free'
  const isPremiumMember = !!currentUser?.can_use_monthly_summary

  // Monthly badge: logsステートから動的計算（currentUserは初回のみのため）
  const now = new Date()
  const thisYear = now.getFullYear()
  const thisMonth = now.getMonth() + 1
  const monthlySessions = React.useMemo(() => {
    const dates = new Set(
      logs
        .filter(l => {
          const d = new Date(l.date)
          return d.getFullYear() === thisYear && d.getMonth() + 1 === thisMonth &&
            l.workout_sets && l.workout_sets.length > 0
        })
        .map(l => l.date)
    )
    return dates.size
  }, [logs, thisYear, thisMonth])
  const monthlyBadge = monthlySessions >= 12 ? 'gold' : monthlySessions >= 8 ? 'silver' : monthlySessions >= 4 ? 'bronze' : 'none'

  // ローディング完了後にベースラインを設定（ページ開時のモーダル防止）
  useEffect(() => {
    if (!loading) {
      prevMonthlyBadge.current = monthlyBadge
      isInitialBadgeLoad.current = false
    }
  }, [loading]) // eslint-disable-line react-hooks/exhaustive-deps

  // バッジ昇格を検出してモーダル表示
  useEffect(() => {
    if (isInitialBadgeLoad.current) return
    if (monthlyBadge !== 'none' && monthlyBadge !== prevMonthlyBadge.current) {
      setEarnedBadge(monthlyBadge)
      setShowBadgeModal(true)
      setMonthlySummary(null)
      setShowSummary(false)
    }
    prevMonthlyBadge.current = monthlyBadge
  }, [monthlyBadge])

  // Form State
  const [exerciseName, setExerciseName] = useState('')
  const [weight, setWeight] = useState('')
  const [reps, setReps] = useState('')
  const [sets, setSets] = useState('')
  const [isBodyweight, setIsBodyweight] = useState(false)
  const [timeMin, setTimeMin] = useState('')
  const [timeSec, setTimeSec] = useState('')
  const [distance, setDistance] = useState('')
  const [floors, setFloors] = useState('')
  const [calories, setCalories] = useState('')
  const [date, setDate] = useState(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })
  const [note, setNote] = useState('')
  const [imageFile, setImageFile] = useState(null)

  // Filter state
  const [selectedBodyPart, setSelectedBodyPart] = useState('脚')
  const [comparisonPeriod, setComparisonPeriod] = useState('week')

  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  // AI Coach State
  const [coachType, setCoachType] = useState('supportive')
  const [isLoadingAdvice, setIsLoadingAdvice] = useState(false)
  const [coachMessages, setCoachMessages] = useState([])
  const [questionText, setQuestionText] = useState('')
  const [isLoadingAnswer, setIsLoadingAnswer] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)

  // Monthly Badge State
  const [showBadgeModal, setShowBadgeModal] = useState(false)
  const [earnedBadge, setEarnedBadge] = useState(null)
  const [monthlySummary, setMonthlySummary] = useState(null)
  const [showSummary, setShowSummary] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [modalSummaryMode, setModalSummaryMode] = useState(false)
  const isInitialBadgeLoad = useRef(true)
  const prevMonthlyBadge = useRef('none')

  // モーダルで総評生成中→完了したらモーダルを閉じてバッジセクションへスクロール
  useEffect(() => {
    if (!modalSummaryMode || summaryLoading) return
    setShowBadgeModal(false)
    setModalSummaryMode(false)
    setTimeout(() => {
      const el = document.getElementById('monthly-badge-section')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
  }, [summaryLoading, modalSummaryMode])

  // Usage summary (AI coach / OCR limits)
  const [usageSummary, setUsageSummary] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/v1/usage_summary')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setUsageSummary(data) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const refreshUsageSummary = () => {
    fetch('/api/v1/usage_summary')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setUsageSummary(data) })
      .catch(() => {})
  }

  // Get today's log for AI advice
  const todayLog = logs.find(l => l.date === date)
  const aiAdvice = todayLog?.ai_advice

  const [humanBodyPeriod, setHumanBodyPeriod] = useState('week')

  const humanBodyVolumeData = React.useMemo(() => {
    let start, end;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (humanBodyPeriod === 'week') {
      const day = today.getDay();
      const diff = today.getDate() - (day === 0 ? 6 : day - 1); // Monday start
      start = new Date(today);
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);

      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      // Month
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
    }

    // Helper to determine if an exercise targets front, back, or both
    const getTargetSide = (name = '', bodyPart = '') => {
      const n = name.toLowerCase();
      
      // Constants/Hardcoded Mapping
      if (n.includes('リアレイズ') || n.includes('リアデルト') || n.includes('フェイスプル')) return 'back';
      if (n.includes('フロントレイズ') || n.includes('ショルダープレス')) return 'front';
      if (n.includes('ベンチプレス') || n.includes('チェストプレス')) return 'front';
      if (n.includes('ラットプル') || n.includes('懸垂') || n.includes('チンニング') || n.includes('ロウ')) return 'back';
      if (n.includes('アームカール') || n.includes('プリチャー')) return 'front';
      if (n.includes('プレスダウン') || n.includes('キックバック') || n.includes('エクステンション')) {
        if (bodyPart === '腕') return 'back'; // Triceps
        if (bodyPart === '脚') return 'front'; // Leg Extension
      }
      if (n.includes('レッグカール') || n.includes('デッドリフト') || n.includes('ヒップ')) return 'back';
      if (n.includes('スクワット') || n.includes('レッグプレス')) return 'front'; // Mostly Quads

      // Heuristics
      if (n.includes('リア') || n.includes('バック') || n.includes('後ろ')) return 'back';
      if (n.includes('フロント') || n.includes('前')) return 'front';
      
      return 'both'; // Default
    };

    const volumeByPart = {};
    logs.forEach(log => {
      const logDate = new Date(log.date);
      if (logDate >= start && logDate <= end) {
        if (log.workout_sets) {
          log.workout_sets.forEach(set => {
            const part = set.exercise ? set.exercise.body_part : 'Unknown';
            if (part === '有酸素運動' || part === 'Unknown') return;

            const name = set.exercise.name || '';
            const side = getTargetSide(name, part);
            
            const setSets = set.sets || 1;
            const effectiveWeight = set.is_bodyweight
              ? (parseFloat(currentUser?.weight) || 60)
              : set.weight
            const isPlankSet = set.exercise?.name === 'プランク' || set.exercise?.en_name === 'Plank'
            if (effectiveWeight && (set.reps || (isPlankSet && set.time))) {
              const volume = isPlankSet
                ? effectiveWeight * set.time * setSets
                : effectiveWeight * set.reps * setSets;
              if (!volumeByPart[part]) volumeByPart[part] = { front: 0, back: 0 };
              
              if (side === 'front') volumeByPart[part].front += volume;
              else if (side === 'back') volumeByPart[part].back += volume;
              else {
                volumeByPart[part].front += volume;
                volumeByPart[part].back += volume;
              }
            }
          });
        }
      }
    });
    return volumeByPart;
  }, [logs, humanBodyPeriod, currentUser]);

  // Q&A thread for the current advice
  useEffect(() => {
    if (!aiAdvice) {
      setCoachMessages([])
      return
    }
    fetch(`/api/v1/ai_coach/messages?date=${date}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data) setCoachMessages(data.messages || []) })
      .catch(e => console.error(e))
  }, [date, aiAdvice])

  const handleAskQuestion = async () => {
    const question = questionText.trim()
    if (!question || isLoadingAnswer) return
    setIsLoadingAnswer(true)
    try {
      const res = await fetch('/api/v1/ai_coach/questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ date, question, locale: i18n.language })
      })

      if (res.status === 429) {
        setIsLoadingAnswer(false)
        alert(t('ai_coach.question_limit_reached'))
        return
      }

      if (res.status !== 202) {
        setIsLoadingAnswer(false)
        alert(t('ai_coach.question_error'))
        return
      }

      const data = await res.json()
      setCoachMessages(prev => [...prev, data.message])
      setQuestionText('')

      const prevCoachCount = coachMessages.filter(m => m.role === 'coach').length
      let polls = 0
      const pollInterval = setInterval(async () => {
        polls += 1
        try {
          const pollRes = await fetch(`/api/v1/ai_coach/messages?date=${date}`)
          if (pollRes.ok) {
            const pollData = await pollRes.json()
            if (pollData.status === 'error') {
              clearInterval(pollInterval)
              setIsLoadingAnswer(false)
              alert(t('ai_coach.question_error'))
              return
            }
            const msgs = pollData.messages || []
            if (msgs.filter(m => m.role === 'coach').length > prevCoachCount) {
              clearInterval(pollInterval)
              setCoachMessages(msgs)
              setIsLoadingAnswer(false)
              return
            }
          }
        } catch (e) {
          console.error(e)
        }
        if (polls >= 20) {
          clearInterval(pollInterval)
          setIsLoadingAnswer(false)
          alert(t('ai_coach.timeout'))
        }
      }, 3000) // Poll every 3 seconds
    } catch (e) {
      console.error(e)
      setIsLoadingAnswer(false)
      alert(t('ai_coach.question_error'))
    }
  }

  const handleFinishSession = async () => {
    if (isFinishing) return
    setIsFinishing(true)
    try {
      const res = await fetch('/api/v1/training_session/finish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ date, locale: i18n.language })
      })

      if (res.status === 200) {
        // Already finished before (idempotent response with the existing comment)
        const data = await res.json()
        setLogs(prev => prev.map(l => l.date === date ? { ...l, ai_one_point: data.one_point, session_ended_at: data.session_ended_at } : l))
        setIsFinishing(false)
        return
      }

      if (res.status !== 202) {
        setIsFinishing(false)
        alert(t('session.error'))
        return
      }

      const accepted = await res.json()
      // Show the summary card immediately; the one-point comment streams in via polling
      setLogs(prev => prev.map(l => l.date === date ? { ...l, session_ended_at: accepted.session_ended_at } : l))

      let polls = 0
      const pollInterval = setInterval(async () => {
        polls += 1
        try {
          const pollRes = await fetch(`/api/v1/training_session?date=${date}`)
          if (pollRes.ok) {
            const data = await pollRes.json()
            if (data.status === 'error') {
              clearInterval(pollInterval)
              setIsFinishing(false)
              alert(t('session.error'))
              return
            }
            if (data.status === 'completed') {
              clearInterval(pollInterval)
              setLogs(prev => prev.map(l => l.date === date ? { ...l, ai_one_point: data.one_point, session_ended_at: data.session_ended_at } : l))
              setIsFinishing(false)
              return
            }
          }
        } catch (e) {
          console.error(e)
        }
        if (polls >= 20) {
          clearInterval(pollInterval)
          setIsFinishing(false)
          alert(t('ai_coach.timeout'))
        }
      }, 3000) // Poll every 3 seconds
    } catch (e) {
      console.error(e)
      setIsFinishing(false)
      alert(t('session.error'))
    }
  }

  const highlightAiCoachButton = () => {
    setTimeout(() => {
      const btn = document.getElementById('ai-coach-button')
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
        btn.style.boxShadow = '0 0 20px rgba(249, 115, 22, 0.8), 0 0 40px rgba(249, 115, 22, 0.4)'
        btn.style.transform = 'scale(1.05)'
        btn.style.transition = 'box-shadow 0.3s, transform 0.3s'
        setTimeout(() => {
          btn.style.boxShadow = ''
          btn.style.transform = ''
        }, 2000)
      }
    }, 300)
  }

  const handleAskAiCoach = async () => {
    setIsLoadingAdvice(true)
    try {
      // 0. Save the note first so the coach job reads the latest impressions from DB
      const noteRes = await fetch('/api/v1/workout_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ workout_log: { date, note } })
      })
      if (noteRes.ok) {
        const savedLog = await noteRes.json()
        setLogs(prevLogs => {
          const existingIndex = prevLogs.findIndex(l => l.id === savedLog.id)
          if (existingIndex >= 0) {
            const updated = [...prevLogs]
            updated[existingIndex] = savedLog
            return updated
          }
          return [savedLog, ...prevLogs]
        })
      }

      // 1. Trigger the job
      const res = await fetch('/api/v1/ai_coach', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ date, coach_type: coachType, locale: i18n.language })
      })

      if (res.status === 429) {
        const data = await res.json()
        setIsLoadingAdvice(false)
        const remaining = Math.max((data.limit ?? 0) - (data.used ?? 0), 0)
        alert(`${data.message || '今月のAIコーチ利用回数が上限に達しました'}\n\n現在のプラン: ${data.plan}\n利用: ${data.used} / ${data.limit}回\n\nプランをアップグレードすると、より多く利用できます。`)
        setIsUpgradeModalOpen(true)
        return
      }

      if (res.status === 202) {
        // 2. Start Polling
        const pollInterval = setInterval(async () => {
          try {
            const pollRes = await fetch(`/api/v1/ai_coach?date=${date}`)
            if (pollRes.ok) {
              const data = await pollRes.json()

              if (data.status === 'completed' && data.advice) {
                clearInterval(pollInterval)

                // Update specific log or state
                // Since advice is not inside a log object here, we might need a separate state for displayed advice
                // Or update the logs array as before.
                // The current component uses `aiAdvice` derived from `todayLog`.
                // So we MUST update `logs`.

                setLogs(prevLogs => {
                  // Ensure we have a log for today (backend job creates it if missing, but frontend needs to know)
                  // We might need to fetch logs again OR manually update.
                  // Since backend created it, let's try to update if it exists in frontend, 
                  // or maybe fetch the single log again?

                  // Simplest: Check if log exists in state.
                  const existingIndex = prevLogs.findIndex(l => l.date === date)
                  if (existingIndex >= 0) {
                    const updated = [...prevLogs]
                    updated[existingIndex] = { ...updated[existingIndex], ai_advice: data.advice }
                    return updated
                  } else {
                    // Log didn't exist in frontend state (page load timing). 
                    // We should probably fetch the logs for the day to get the full object (id, etc).
                    // But for now, let's just create a temporary object so it shows up?
                    // No, that might break keys.
                    // Better: fetch the logs again for this date.
                    fetchInitialData() // Crude but effective? Or just specific log fetch.
                    return prevLogs
                  }
                })

                setIsLoadingAdvice(false)
                refreshUsageSummary()
              } else if (data.status === 'error') {
                clearInterval(pollInterval)
                setIsLoadingAdvice(false)
                alert(t('ai_coach.error'))
              }
              // If status is 'processing', do nothing, keep polling.
            } else {
              // Poll failed?
              console.error("Poll failed")
            }
          } catch (e) {
            console.error("Polling error", e)
          }
        }, 3000) // Poll every 3 seconds

        // Timeout after 3 minutes
        setTimeout(() => {
          clearInterval(pollInterval)
          if (isLoadingAdvice) { // If still loading
            setIsLoadingAdvice(false)
            alert(t('ai_coach.timeout'))
          }
        }, 180000)

      } else {
        const data = await res.json()
        alert(data.error || t('error_occurred'))
        setIsLoadingAdvice(false)
      }
    } catch (e) {
      console.error(e)
      alert(t('error_occurred'))
      setIsLoadingAdvice(false)
    }
  }

  const JP_TO_KEY = {
    '脚': 'legs', '腕': 'arms', '肩': 'shoulders', '背中': 'back', '胸': 'chest', '腹筋': 'abs', '有酸素運動': 'cardio'
  }
  const getBodyPartLabel = (jpName) => {
    const key = JP_TO_KEY[jpName]
    return key ? t(`body_parts.${key}`) : jpName
  }


  const filteredExercises = exercises.filter(e => e.body_part === selectedBodyPart)

  const dailyLogs = logs.filter(l => l.date === date)
  const hasWorkoutSets = dailyLogs.some(l => l.workout_sets && l.workout_sets.length > 0)

  // Training session finish flow (free one-point comment, not counted against AI limits)
  // A session crossing midnight can still be finished until 3:00 the next day
  const isSessionFinishableDate = (() => {
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const now = new Date()
    if (date === fmt(now)) return true
    if (now.getHours() >= 3) return false
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    return date === fmt(yesterday)
  })()
  const sessionSets = dailyLogs.flatMap(l => l.workout_sets || [])
  const sessionVolume = Math.round(sessionSets.reduce((sum, s) => sum + ((s.weight || 0) * (s.reps || 0)), 0))
  const sessionExerciseCount = new Set(sessionSets.filter(s => s.exercise).map(s => s.exercise.id)).size
  const sessionDurationMinutes = (() => {
    if (!todayLog?.session_ended_at) return null
    const times = sessionSets.map(s => new Date(s.created_at).getTime()).filter(ts => !isNaN(ts))
    if (times.length === 0) return null
    const minutes = Math.round((new Date(todayLog.session_ended_at).getTime() - Math.min(...times)) / 60000)
    return minutes >= 10 ? minutes : null
  })()

  const selectedExercise = exercises.find(e => e.name === exerciseName || e.en_name === exerciseName)
  const isCardio = selectedExercise?.body_part === '有酸素運動'
  const isRunning = isCardio && (selectedExercise?.name === 'ランニング' || selectedExercise?.en_name === 'Running')
  const isStairClimber = isCardio && (selectedExercise?.name === 'ステアクライマー' || selectedExercise?.en_name === 'Stair Climber')
  const isPlank = !isCardio && (selectedExercise?.name === 'プランク' || selectedExercise?.en_name === 'Plank')

  const progressivePRs = React.useMemo(() => {
    const isPrMap = {}
    const runningPrs = {}

    // Sort logs chronologically (oldest first)
    const sortedLogs = [...logs].sort((a, b) => a.date.localeCompare(b.date))

    sortedLogs.forEach(log => {
      if (!log.workout_sets) return
      // We also sort sets within log by ID (assuming ID means creation order) 
      const sortedSets = [...log.workout_sets].sort((a, b) => a.id - b.id)

      sortedSets.forEach(set => {
        const exName = set.exercise?.name || set.exercise?.en_name
        if (!exName || !set.weight) return

        if (!runningPrs[exName] || Number(set.weight) > runningPrs[exName]) {
          runningPrs[exName] = Number(set.weight)
          isPrMap[set.id] = true
        }
      })
    })

    return isPrMap
  }, [logs])

  const [historyCandidates, setHistoryCandidates] = useState([])
  const [exerciseChartData, setExerciseChartData] = useState([])
  const [exerciseYearChartData, setExerciseYearChartData] = useState([])
  const [chartPeriod, setChartPeriod] = useState('recent')

  // Rest timer
  const [restSeconds, setRestSeconds] = useState(currentUser?.rest_seconds ?? 90)
  const [restRemaining, setRestRemaining] = useState(null)
  const [restDone, setRestDone] = useState(false)
  const restEndTimeRef = useRef(null)
  const restWakeLockRef = useRef(null)

  // Plank timer
  const [isPlankTimerRunning, setIsPlankTimerRunning] = useState(false)
  const [plankElapsed, setPlankElapsed] = useState(0)
  const [plankWeightError, setPlankWeightError] = useState(false)
  const plankTimerRef = useRef(null)
  const plankWakeLockRef = useRef(null)

  const requestRestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        restWakeLockRef.current = await navigator.wakeLock.request('screen')
      } catch (_err) { /* 非対応端末は無視 */ }
    }
  }

  const releaseRestWakeLock = () => {
    if (restWakeLockRef.current) {
      restWakeLockRef.current.release()
      restWakeLockRef.current = null
    }
  }

  useEffect(() => {
    if (restRemaining === null || restRemaining < 0) return
    if (restRemaining === 0) {
      setRestDone(true)
      releaseRestWakeLock()
      if (navigator.vibrate) navigator.vibrate([500, 200, 500])
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(t('rest_timer_done'), { icon: '/icon-v2.png', silent: true })
      }
      return
    }
    const id = setTimeout(() => setRestRemaining(r => r - 1), 1000)
    return () => clearTimeout(id)
  }, [restRemaining])

  // 画面復帰時にタイムスタンプから残り時間を再計算する
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && restEndTimeRef.current !== null) {
        const remaining = Math.ceil((restEndTimeRef.current - Date.now()) / 1000)
        setRestRemaining(remaining <= 0 ? 0 : remaining)
        requestRestWakeLock()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const startRestTimer = (seconds) => {
    setRestDone(false)
    restEndTimeRef.current = Date.now() + seconds * 1000
    setRestRemaining(seconds)
    requestRestWakeLock()
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }

  const stopRestTimer = () => {
    setRestRemaining(null)
    setRestDone(false)
    restEndTimeRef.current = null
    releaseRestWakeLock()
  }

  const requestPlankWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try { plankWakeLockRef.current = await navigator.wakeLock.request('screen') } catch (_) {}
    }
  }

  const releasePlankWakeLock = () => {
    if (plankWakeLockRef.current) { plankWakeLockRef.current.release(); plankWakeLockRef.current = null }
  }

  const handlePlankStart = () => {
    if (!isBodyweight && (!weight || parseFloat(weight) <= 0)) {
      setPlankWeightError(true)
      return
    }
    setPlankWeightError(false)
    setPlankElapsed(0)
    setIsPlankTimerRunning(true)
    requestPlankWakeLock()
  }

  const handlePlankStop = async () => {
    clearInterval(plankTimerRef.current)
    plankTimerRef.current = null
    releasePlankWakeLock()
    setIsPlankTimerRunning(false)
    const totalSecs = plankElapsed
    if (totalSecs === 0) return
    const exercise = exercises.find(e => e.name === exerciseName || e.en_name === exerciseName)
    if (!exercise) return
    const workoutSetsAttributes = [{
      exercise_id: exercise.id,
      weight: isBodyweight ? null : (weight ? parseFloat(weight) : null),
      is_bodyweight: isBodyweight,
      reps: null,
      sets: 1,
      time: totalSecs / 60,
      distance: null,
      floors: null,
      calories: null
    }]
    try {
      const res = await fetch('/api/v1/workout_logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content },
        body: JSON.stringify({ workout_log: { date, note, workout_sets_attributes: workoutSetsAttributes } })
      })
      if (res.ok) {
        const newLog = await res.json()
        setLogs(prevLogs => {
          const existingIndex = prevLogs.findIndex(l => l.id === newLog.id)
          if (existingIndex >= 0) { const updated = [...prevLogs]; updated[existingIndex] = newLog; return updated }
          return [newLog, ...prevLogs]
        })
        setIsBodyweight(false)
        setExerciseName('')
        setWeight('')
        setPlankElapsed(0)
        if (restSeconds > 0) startRestTimer(restSeconds)
        if (newLog.new_badge_achieved) {
          setEarnedBadge(newLog.new_badge_achieved)
          setShowBadgeModal(true)
          setMonthlySummary(null)
          setShowSummary(false)
        }
      }
    } catch (err) { console.error(err) }
  }

  const fetchMonthlySummary = async (badgeLevel) => {
    const now = new Date()
    const cacheKey = `monthly_summary_${now.getFullYear()}_${now.getMonth() + 1}_${badgeLevel}`
    const pendingKey = `${cacheKey}_pending`
    const csrf = document.querySelector('meta[name="csrf-token"]').content

    // 前回DB保存が失敗していた場合はまずリトライ保存
    const pending = localStorage.getItem(pendingKey)
    if (pending) {
      try {
        const res = await fetch('/api/v1/monthly_summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
          body: JSON.stringify({ badge_level: badgeLevel, content: pending })
        })
        const data = await res.json()
        if (data.saved) localStorage.removeItem(pendingKey)
      } catch (err) { console.error(err) }
    }

    setSummaryLoading(true)
    try {
      const res = await fetch(`/api/v1/monthly_summary?badge_level=${badgeLevel}`, {
        headers: { 'X-CSRF-Token': csrf }
      })
      const data = await res.json()
      if (data.content) {
        localStorage.setItem(cacheKey, data.content)
        if (data.saved === false) localStorage.setItem(pendingKey, data.content)
        setMonthlySummary(data.content)
      } else {
        const cached = localStorage.getItem(cacheKey)
        setMonthlySummary(cached || null)
      }
      setShowSummary(true)
    } catch (err) {
      console.error(err)
      const cached = localStorage.getItem(cacheKey)
      if (cached) { setMonthlySummary(cached); setShowSummary(true) }
    }
    setSummaryLoading(false)
  }

  const badgeLabel = (level) => ['bronze', 'silver', 'gold'].includes(level) ? t(`badge.${level}`) : ''
  const badgeColor = (level) => ({ bronze: 'text-amber-600', silver: 'text-gray-400', gold: 'text-yellow-400' }[level] || '')
  const badgeThreshold = (level) => ({ bronze: 4, silver: 8, gold: 12 }[level] || 0)
  const nowForBadge = new Date()
  const monthStr = String(nowForBadge.getMonth() + 1).padStart(2, '0')

  useEffect(() => {
    if (!isPlankTimerRunning) return
    plankTimerRef.current = setInterval(() => setPlankElapsed(prev => prev + 1), 1000)
    return () => clearInterval(plankTimerRef.current)
  }, [isPlankTimerRunning])

  useEffect(() => {
    // When exerciseName changes (and is not empty), find last 2 distinct days
    // Always stop plank timer when switching exercises
    if (plankTimerRef.current) { clearInterval(plankTimerRef.current); plankTimerRef.current = null }
    releasePlankWakeLock()
    setIsPlankTimerRunning(false)
    setPlankElapsed(0)

    if (!exerciseName) {
      setHistoryCandidates([])
      setExerciseChartData([])
      setExerciseYearChartData([])
      setIsBodyweight(false)
      return
    }

    // If cardio, do not show history. Just show placeholder (clear inputs).
    if (isCardio) {
      setHistoryCandidates([])
      setExerciseChartData([])
      setExerciseYearChartData([])
      setTimeMin('')
      setTimeSec('')
      setDistance('')
      setFloors('')
      setCalories('')
      setWeight('')
      setReps('')
      setSets('')
      return
    }

    if (isPlank) {
      setHistoryCandidates([])
      setExerciseChartData([])
      setExerciseYearChartData([])
      setTimeMin('')
      setTimeSec('')
      setWeight('')
      setReps('')
      setSets('')
      return
    }

    // Safety check if logs are loaded
    if (logs.length === 0) return

    // Find all sets for this exercise, excluding current date (optional, but safer for "previous history")
    // If user wants to "copy from today's earlier set", we might include today. 
    // "Previous time" usually means previous session.
    // Let's filter out logs with date === date (current form date).
    const relevantLogs = logs.filter(l => l.date !== date).sort((a, b) => b.date.localeCompare(a.date))

    // We need to check if these logs actually contain the chosen exercise
    const logsWithExercise = relevantLogs.filter(l =>
      l.workout_sets && l.workout_sets.some(s => {
        // Match exercise name
        const ex = s.exercise
        if (!ex) return false
        // Check if name matches (either japanese or english)
        // exerciseName in state is the *selected* name (which depends on current locale or input)
        // Usually exerciseName comes from ExerciseSelector which returns the name.
        // Let's check against s.exercise.name and s.exercise.en_name
        return ex.name === exerciseName || ex.en_name === exerciseName
      })
    )

    // Take top 10 sessions
    const recentLogs = logsWithExercise.slice(0, 10)

    if (recentLogs.length === 0) {
      setHistoryCandidates([])
      setExerciseChartData([])
      setExerciseYearChartData([])
      setWeight('')
      setReps('')
      setSets('')
      return
    }

    // Build chart data (oldest → newest)
    const chartData = [...recentLogs].reverse().map(log => {
      const weights = (log.workout_sets || [])
        .filter(s => s.exercise && (s.exercise.name === exerciseName || s.exercise.en_name === exerciseName) && s.weight !== null)
        .map(s => parseFloat(s.weight))
      const maxW = weights.length > 0 ? Math.max(...weights) : null
      const [, m, d] = log.date.split('-')
      return { label: `${parseInt(m)}/${parseInt(d)}`, weight: maxW }
    }).filter(p => p.weight !== null)
    setExerciseChartData(chartData)

    // 1年表示用: 過去12ヶ月の月別最大重量（プレミアム限定表示）
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
    const monthlyMax = new Map()
    logsWithExercise.forEach(log => {
      if (new Date(log.date) < oneYearAgo) return
      const weights = (log.workout_sets || [])
        .filter(s => s.exercise && (s.exercise.name === exerciseName || s.exercise.en_name === exerciseName) && s.weight !== null)
        .map(s => parseFloat(s.weight))
      if (weights.length === 0) return
      const maxW = Math.max(...weights)
      const key = log.date.slice(0, 7)
      if (!monthlyMax.has(key) || monthlyMax.get(key) < maxW) monthlyMax.set(key, maxW)
    })
    const yearChartData = [...monthlyMax.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, weight]) => {
        const [y, m] = key.split('-')
        return { label: new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString(i18n.language, { month: 'short' }), weight }
      })
    setExerciseYearChartData(yearChartData)

    // Extract ALL sets from these sessions
    const allSets = recentLogs.flatMap(log =>
      log.workout_sets ? log.workout_sets.filter(s => {
        const ex = s.exercise
        if (!ex) return false
        return ex.name === exerciseName || ex.en_name === exerciseName
      }) : []
    ).filter(s => s.weight !== null && s.weight !== undefined)

    if (allSets.length === 0) {
      setHistoryCandidates([])
      setWeight('')
      setReps('')
      setSets('')
      return
    }

    // Group by weight and pick the best set (max reps) for each weight
    // This ensures that if we have 100kgx10 and 100kgx5, we keep 100kgx10 as the representative for 100kg.
    const bestSetsByWeight = {}
    allSets.forEach(s => {
      const w = parseFloat(s.weight)
      if (!bestSetsByWeight[w] || s.reps > bestSetsByWeight[w].reps) {
        bestSetsByWeight[w] = s
      }
    })

    // Convert to array and sort by weight descending
    const candidates = Object.values(bestSetsByWeight).sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight))

    setHistoryCandidates(candidates)

    // Auto-fill Logic based on User Preferences
    if (candidates.length > 0) {
      let candidateToUse = candidates[0] // Default to heaviest

      // If user wants min weight, use lightest
      if (currentUser?.show_min_weight) {
        candidateToUse = candidates[candidates.length - 1]
      }

      fillFormWithSet(candidateToUse)
    } else {
      setWeight('')
      setReps('')
      setSets('')
    }

  }, [exerciseName, logs, date]) // Re-run if date changes (to exclude it) or logs update

  const fillFormWithSet = (set) => {
    if (!set) return
    setWeight(set.weight)
    setReps(set.reps)
    setSets(set.sets || 1) // default to 1 if missing in old data, though current logic saves it
  }

  const fillHeavier = () => {
    if (historyCandidates.length === 0) return
    const currentW = parseFloat(weight)
    if (isNaN(currentW)) return

    // Find the candidate with the smallest weight that is GREATER than current weight
    // candidates are sorted DESC (100, 90, 80)
    // We want the last one that is > current
    const heavierCandidates = historyCandidates.filter(c => parseFloat(c.weight) > currentW)
    if (heavierCandidates.length > 0) {
      // The last element in DESC sorted list that is > current is the one just above current
      fillFormWithSet(heavierCandidates[heavierCandidates.length - 1])
    }
  }

  const fillLighter = () => {
    if (historyCandidates.length === 0) return
    const currentW = parseFloat(weight)
    if (isNaN(currentW)) return

    // Find the candidate with the largest weight that is LESS than current weight
    // Candidates [100, 95, 90]
    // Filter w < current (95) -> [90]
    // The first element in DESC sorted list that is < current is the one just below current
    const lighterCandidates = historyCandidates.filter(c => parseFloat(c.weight) < currentW)
    if (lighterCandidates.length > 0) {
      fillFormWithSet(lighterCandidates[0])
    }
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    // When date changes or logs load, update note from the first log found for that date
    // With "One Log Per Day", there should be only one or none.
    // If multiple logs (legacy), we just take the first note found.
    const logForDate = logs.find(l => l.date === date)
    setNote(logForDate?.note || '')
  }, [date, logs])

  const fetchInitialData = async () => {
    try {
      const [logsRes, exercisesRes] = await Promise.all([
        fetch('/api/v1/workout_logs', { cache: 'no-store' }),
        fetch('/api/v1/exercises', { cache: 'no-store' })
      ])

      if (logsRes.ok) {
        const data = await logsRes.json()
        setLogs(data)
      }
      if (exercisesRes.ok) {
        const data = await exercisesRes.json()
        setExercises(data)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }



  // iOS(PWA)でEnterを押すとフォームが暗黙送信され、記録追加＋レイアウト変化でスクロール位置が飛ぶ。
  // Enterはキーボードを閉じるだけにして、記録追加は「記録を追加」ボタンに限定する。
  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter') return
    if (e.target.tagName !== 'INPUT') return
    e.preventDefault()
    e.target.blur()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const exercise = exercises.find(e => e.name === exerciseName || e.en_name === exerciseName)
    if (!exercise) {
      alert(t('select_exercise'))
      return
    }

    const isCardio = exercise.body_part === '有酸素運動'
    const isPlank = !isCardio && (exercise.name === 'プランク' || exercise.en_name === 'Plank')


    // Validation
    if (isCardio || isPlank) {
      // 時間は必須（距離・カロリー・階数は任意）
      const totalSec = (parseInt(timeMin) || 0) * 60 + (parseInt(timeSec) || 0)
      if (totalSec <= 0) {
        alert(t('input_time_required'))
        return
      }
    } else {
      if ((!isBodyweight && !weight) || !reps || !sets) {
        alert(t('input_weight_requirements'))
        return
      }
      if (!isBodyweight && (parseFloat(weight) < 0 || parseInt(reps) <= 0 || parseInt(sets) <= 0)) {
        alert(t('input_positive_requirements'))
        return
      }
      if (isBodyweight && (parseInt(reps) <= 0 || parseInt(sets) <= 0)) {
        alert(t('input_positive_requirements'))
        return
      }
    }

    // Shared logic for both Cardio and Weights
    // Cardio input doesn't have sets UI normally, but we default to 1 or value
    // Cardio usually is just one set of X mins or Y km.
    // If user inputs "2 sets" of cardio, we can store `sets: 2`.
    // But currently cardio form doesn't really have "sets". It uses `set_number` for ordering if needed.
    // Let's assume standard weight/reps logic for now.

    // Previous logic: loop `sets` times and create `sets` number of objects.
    // New logic: Create ONE object with `sets: setsValue`.

    const setsVal = sets ? parseInt(sets) : 1

    const workoutSetsAttributes = [
      {
        exercise_id: exercise.id,
        weight: isCardio ? null : (isBodyweight ? null : parseFloat(weight)),
        is_bodyweight: !isCardio && isBodyweight,
        reps: (isCardio || isPlank) ? null : parseInt(reps),
        sets: (isCardio || isPlank) ? 1 : setsVal,
        time: (isCardio || isPlank) && (timeMin || timeSec) ? (parseInt(timeMin || 0) + (parseInt(timeSec || 0) / 60)) : null,
        distance: isCardio && distance ? parseFloat(distance) : null,
        floors: isCardio && isStairClimber && floors ? parseInt(floors) : null,
        calories: isCardio && calories ? parseInt(calories) : null
      }
    ]

    const payload = {
      workout_log: {
        date: date,
        note: note, // Send note with every update
        workout_sets_attributes: workoutSetsAttributes
      }
    }

    try {
      const res = await fetch('/api/v1/workout_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      })

      if (res.ok) {
        const newLog = await res.json()
        // Ideally we should merge this effectively, but for now re-fetch or prepend
        // The API returns the log with included sets
        setLogs(prevLogs => {
          // If we are updating an existing log (same id), replace it.
          // If we created a new log (new id), prepend it.
          // The API now returns the *entire* updated log for the day.
          const existingIndex = prevLogs.findIndex(l => l.id === newLog.id)
          if (existingIndex >= 0) {
            const updated = [...prevLogs]
            updated[existingIndex] = newLog
            return updated
          }
          return [newLog, ...prevLogs]
        })

        if (!isCardio && restSeconds > 0) startRestTimer(restSeconds)
        setIsBodyweight(false)

        // NOTE: We don't reset 'note' here because it's part of the day's record.
        // We persist it.
        setExerciseName('')
        setWeight('')
        setReps('')
        setSets('')
        setTimeMin('')
        setTimeSec('')
        setDistance('')
        setFloors('')
        setCalories('')
        setImageFile(null)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDelete = async (logId) => {
    if (!confirm(t('confirm_delete'))) return

    try {
      const res = await fetch(`/api/v1/workout_logs/${logId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (res.ok) {
        setLogs(logs.filter(l => l.id !== logId))
      } else {
        const errorData = await res.json()
        console.error('Failed to delete log', errorData)
        alert(t('delete_failed', { error: errorData.error }))
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleSaveRun = async (runData) => {
    if (!selectedExercise) return

    const workoutSetsAttributes = [
      {
        exercise_id: selectedExercise.id,
        distance: parseFloat(runData.distance),
        time: runData.duration / 60,
        calories: parseInt(runData.calories),
        route_data: runData.path,
        sets: 1
      }
    ]

    const payload = {
      workout_log: {
        date: date,
        note: note,
        workout_sets_attributes: workoutSetsAttributes
      }
    }

    try {
      const res = await fetch('/api/v1/workout_logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      })

      if (res.ok) {
        const newLog = await res.json()
        setLogs(prevLogs => {
          const existingIndex = prevLogs.findIndex(l => l.id === newLog.id)
          if (existingIndex >= 0) {
            const updated = [...prevLogs]
            updated[existingIndex] = newLog
            return updated
          }
          return [newLog, ...prevLogs]
        })
        setExerciseName('')
        alert(t('success_saved'))
      }
    } catch (err) {
      console.error(err)
      alert(t('error_occurred'))
    }
  }

  if (loading) return <div className="text-white p-4">Loading...</div>

  return (
    <div className="min-h-screen bg-[#111] text-white p-4 font-sans max-w-2xl mx-auto relative">
      <header className="relative flex justify-center items-center mb-8 mt-4">
        <div className="flex items-center gap-2">
          <DumbbellIcon className="w-8 h-8 text-orange-500" />
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-500">
            Anytime Tracker
          </h1>
        </div>
        <button
          onClick={onOpenMenu}
          className="text-gray-400 hover:text-white transition-colors flex flex-col items-center gap-0.5 absolute right-[-1px] top-1/2 -translate-y-1/2"
        >
          <div className="relative">
            <User color="#CCCCCC" />
            <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#111]"></div>
          </div>
          <span className="text-[10px] font-bold">{t('menu')}</span>
        </button>



      </header >

      <TrialBanner currentUser={currentUser} />

      {currentUser?.show_weekly_plan !== false && <WeeklyPlanCard />}

      <GoalPromptBanner currentUser={currentUser} />



      <section className="mb-8">
        <div className="flex justify-end items-center mb-4">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="text-orange-500 hover:text-orange-400 text-xs sm:text-sm flex items-center gap-1 bg-[#2c2c2e] px-2 sm:px-3 py-1.5 rounded-lg border border-orange-500/30 font-bold transition-colors whitespace-nowrap shrink-0"
            type="button"
          >
            <Camera className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500 shrink-0" />
            {t('import_from_image')}
          </button>
        </div>
        <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-4">
            <div>


              <label htmlFor="body_part_select" className="block text-gray-400 text-sm mb-1">{t('filter_by_part')}</label>
              <div className="relative mb-4">
                <select
                  id="body_part_select"
                  value={selectedBodyPart}
                  onChange={(e) => setSelectedBodyPart(e.target.value)}
                  className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                >
                  <option value="脚">{t('body_parts.legs')}</option>
                  <option value="腕">{t('body_parts.arms')}</option>
                  <option value="肩">{t('body_parts.shoulders')}</option>
                  <option value="背中">{t('body_parts.back')}</option>
                  <option value="胸">{t('body_parts.chest')}</option>
                  <option value="腹筋">{t('body_parts.abs')}</option>
                  <option value="有酸素運動">{t('body_parts.cardio')}</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                </div>
              </div>

              <label className="block text-gray-400 text-sm mb-1">{t('select_exercise')}</label>
              <ExerciseSelector
                exercises={filteredExercises}
                selectedName={exerciseName}
                onSelect={(name) => setExerciseName(name)}
                onEdit={(exercise) => {
                  // This is where we might need to handle edit if ExerciseSelector supports it, 
                  // but usually edit is from ExerciseList (menu).
                  // Wait, ExerciseList is NOT rendered here? 
                  // Ah, onOpenMenu opens the menu which probably renders ExerciseList?
                  // No, Dashboard receives onOpenMenu.
                  // Let's assume onOpenMenu opens a sidebar that contains ExerciseList?
                  // Actually, I don't see ExerciseList in Dashboard. 
                  // It must be in App.jsx! 
                }}
              />

              <div className="mt-2">
                <input
                  id="exercise_name_input"
                  type="text"
                  value={exerciseName}
                  readOnly
                  className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                  placeholder={t('select_exercise_placeholder')}
                />
              </div>

              {/* Exercise weight trend chart */}
              {exerciseChartData.length >= 2 && (() => {
                const displayedChartData = chartPeriod === 'year' ? exerciseYearChartData : exerciseChartData
                return (
                <div className="mt-3 bg-[#2c2c2e] rounded-xl px-2 pt-3 pb-1">
                  <div className="flex items-center justify-between pl-2 pr-2 mb-0.5">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setChartPeriod('recent')}
                        className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${chartPeriod === 'recent' ? 'bg-orange-500 text-black font-bold' : 'bg-[#1c1c1e] text-gray-400'}`}
                      >
                        {t('chart_period_recent')}
                      </button>
                      <button
                        type="button"
                        onClick={() => isPremiumMember ? setChartPeriod('year') : setIsUpgradeModalOpen(true)}
                        className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${chartPeriod === 'year' ? 'bg-orange-500 text-black font-bold' : 'bg-[#1c1c1e] text-gray-400'}`}
                      >
                        {!isPremiumMember && <span className="mr-0.5">🔒</span>}{t('chart_period_year')}
                      </button>
                    </div>
                    <p className="text-gray-500 text-[10px]">{t('weight_trend_label')} (kg)</p>
                  </div>
                  <ResponsiveContainer width="100%" height={110}>
                    <LineChart data={displayedChartData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                      <XAxis
                        dataKey="label"
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        interval={Math.max(0, Math.floor(displayedChartData.length / 5))}
                      />
                      <YAxis
                        width={32}
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickCount={3}
                        domain={([min, max]) => [Math.floor(min - 2), Math.ceil(max + 2)]}
                      />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1c1c1e', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }}
                        labelStyle={{ color: '#9ca3af' }}
                        itemStyle={{ color: '#f97316' }}
                        formatter={(v) => [`${v} kg`, '']}
                      />
                      <Line
                        type="monotone"
                        dataKey="weight"
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={(props) => {
                          const isLast = props.index === displayedChartData.length - 1
                          return <circle key={props.index} cx={props.cx} cy={props.cy} r={isLast ? 5 : 3} fill={isLast ? '#f97316' : '#f9731699'} stroke="none" />
                        }}
                        activeDot={{ r: 5, fill: '#f97316' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                )
              })()}


            </div>



            {isRunning ? (
              <div className="mt-4 min-h-[500px]">
                <LiveTracker
                  userWeight={currentUser?.weight}
                  onSave={handleSaveRun}
                />
              </div>
            ) : isPlank ? (
              isPlankTimerRunning ? (
                <div className="flex flex-col items-center gap-6 py-8">
                  <p className="text-gray-400 text-sm font-bold tracking-widest uppercase">プランク 計測中</p>
                  <div className="text-7xl font-mono font-bold text-orange-500 tabular-nums">
                    {String(Math.floor(plankElapsed / 60)).padStart(2, '0')}:{String(plankElapsed % 60).padStart(2, '0')}
                  </div>
                  <button
                    type="button"
                    onClick={handlePlankStop}
                    className="flex items-center justify-center gap-3 bg-slate-100 hover:bg-white text-slate-900 px-10 py-4 rounded-full font-bold text-lg shadow-lg transition-all active:scale-95 whitespace-nowrap"
                  >
                    <Square fill="currentColor" size={22} /> 停止して保存
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor="weight_plank" className="text-gray-400 text-sm">{t('weight_kg')}</label>
                        <button
                          type="button"
                          onClick={() => { setIsBodyweight(b => !b); setWeight(''); setPlankWeightError(false) }}
                          className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-colors ${isBodyweight ? 'bg-orange-500 border-orange-500 text-black' : 'border-gray-600 text-gray-400 hover:border-orange-500 hover:text-orange-400'}`}
                        >{t('bodyweight')}</button>
                      </div>
                      {isBodyweight ? (
                        <div className="w-full bg-[#2c2c2e] border border-orange-500/50 rounded-lg p-3 text-orange-400 font-bold flex items-center justify-between">
                          <span>{t('bodyweight')}</span>
                          {!currentUser?.weight && <span className="text-xs text-gray-500 font-normal">60kgで計算</span>}
                        </div>
                      ) : (
                        <input
                          id="weight_plank"
                          type="number"
                          placeholder="0"
                          value={weight}
                          onChange={(e) => { setWeight(e.target.value); setPlankWeightError(false) }}
                          step="0.5"
                          className={`w-full bg-[#2c2c2e] border rounded-lg p-3 text-white focus:outline-none transition-colors ${plankWeightError ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                        />
                      )}
                      {plankWeightError && (
                        <p className="text-red-400 text-xs mt-1">重量か自重を選択してください</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="date_plank" className="block text-gray-400 text-sm mb-1">{t('date')}</label>
                      <input
                        id="date_plank"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors pointer-events-auto appearance-none text-left"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handlePlankStart}
                    className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-2"
                  >
                    スタート
                  </button>
                </div>
              )
            ) : isCardio ? (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="time_min" className="block text-gray-400 text-sm mb-1">{t('time_min')}</label>
                    <input
                      id="time_min"
                      type="number"
                      placeholder="30"
                      value={timeMin}
                      onChange={(e) => setTimeMin(e.target.value)}
                      className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="time_sec" className="block text-gray-400 text-sm mb-1">{t('time_sec')}</label>
                    <input
                      id="time_sec"
                      type="number"
                      placeholder="00"
                      value={timeSec}
                      onChange={(e) => setTimeSec(e.target.value)}
                      className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>

                  <div>
                    {isStairClimber ? (
                      <>
                        <label htmlFor="floors" className="block text-gray-400 text-sm mb-1">{t('floors')}</label>
                        <input
                          id="floors"
                          type="number"
                          placeholder="50"
                          value={floors}
                          onChange={(e) => setFloors(e.target.value)}
                          className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                        />
                      </>
                    ) : (
                      <>
                        <label htmlFor="distance" className="block text-gray-400 text-sm mb-1">{t('distance_km')}</label>
                        <input
                          id="distance"
                          type="number"
                          placeholder="5.0"
                          value={distance}
                          onChange={(e) => setDistance(e.target.value)}
                          step="0.1"
                          className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="col-span-1">
                    <label htmlFor="calories" className="block text-gray-400 text-sm mb-1">{t('calories')}</label>
                    <input
                      id="calories"
                      type="number"
                      placeholder="300"
                      value={calories}
                      onChange={(e) => setCalories(e.target.value)}
                      className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="date_cardio" className="block text-gray-400 text-sm mb-1">{t('date')}</label>
                    <input
                      id="date_cardio"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors pointer-events-auto appearance-none text-left"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label htmlFor="weight" className="text-gray-400 text-sm">{t('weight_kg')}</label>
                        <button
                          type="button"
                          onClick={() => { setIsBodyweight(b => !b); setWeight('') }}
                          className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-colors ${isBodyweight ? 'bg-orange-500 border-orange-500 text-black' : 'border-gray-600 text-gray-400 hover:border-orange-500 hover:text-orange-400'}`}
                        >{t('bodyweight')}</button>
                      </div>
                      {isBodyweight ? (
                        <div className="w-full bg-[#2c2c2e] border border-orange-500/50 rounded-lg p-3 text-orange-400 font-bold flex items-center justify-between">
                          <span>{t('bodyweight')}</span>
                          {!currentUser?.weight && <span className="text-xs text-gray-500 font-normal">60kgで計算</span>}
                        </div>
                      ) : (
                        <input
                          id="weight"
                          type="number"
                          placeholder="50"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                          step="0.5"
                          className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                        />
                      )}
                    </div>
                    <div>
                      <label htmlFor="reps" className="block text-gray-400 text-sm mb-1">{t('reps')}</label>
                      <input
                        id="reps"
                        type="number"
                        placeholder="10"
                        value={reps}
                        onChange={(e) => setReps(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <label htmlFor="sets" className="block text-gray-400 text-sm mb-1">{t('sets')}</label>
                      <input
                        id="sets"
                        type="number"
                        placeholder="3"
                        value={sets}
                        onChange={(e) => setSets(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors"
                      />
                    </div>
                    <div className="col-span-2">
                      <label htmlFor="date" className="block text-gray-400 text-sm mb-1">{t('date')}</label>
                      <input
                        id="date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors pointer-events-auto appearance-none text-left"
                        style={{ colorScheme: 'dark' }}
                      />
                    </div>
                  </div>
                </div>
                {/* History Up/Down Buttons */}
                {historyCandidates.length > 0 && (
                  <div className="flex flex-col justify-center gap-2">
                    <button
                      type="button"
                      onClick={fillHeavier}
                      className="p-3 bg-[#2c2c2e] hover:bg-[#3a3a3c] rounded-lg text-orange-500 transition-colors h-full flex items-center justify-center"
                      title="Use heavier weight from history"
                    >
                      <ChevronUp className="w-6 h-6" />
                    </button>
                    <button
                      type="button"
                      onClick={fillLighter}
                      className="p-3 bg-[#2c2c2e] hover:bg-[#3a3a3c] rounded-lg text-orange-500 transition-colors h-full flex items-center justify-center"
                      title="Use lighter weight from history"
                    >
                      <ChevronDown className="w-6 h-6" />
                    </button>
                  </div>
                )}
              </div>
            )}


            {!isRunning && !isPlank && (
              <button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-4"
              >
                <PlusIcon className="w-5 h-5" />
                {t('add_log')}
              </button>
            )}

            {/* Rest Timer */}
            {restRemaining !== null && (
              <div className={`mt-3 rounded-xl overflow-hidden border ${restDone ? 'border-orange-400 bg-orange-500' : 'border-gray-700 bg-[#2c2c2e]'} transition-colors duration-500`}>
                {!restDone && (
                  <div className="h-1 bg-gray-700">
                    <div
                      className="h-1 bg-orange-500 transition-all duration-1000"
                      style={{ width: `${(restRemaining / restSeconds) * 100}%` }}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-3">
                  <span className={`text-sm font-bold ${restDone ? 'text-black' : 'text-gray-400'}`}>
                    {restDone ? t('rest_timer_done') : t('rest_timer_label')}
                  </span>
                  <div className="flex items-center gap-3">
                    {!restDone && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setRestSeconds(s => Math.max(30, s - 30)); setRestRemaining(r => { const next = Math.max(0, r - 30); if (restEndTimeRef.current) restEndTimeRef.current -= 30000; return next }); }}
                          className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
                        >－30</button>
                        <span className="text-2xl font-mono font-bold text-orange-400 w-16 text-center">
                          {String(Math.floor(restRemaining / 60)).padStart(2, '0')}:{String(restRemaining % 60).padStart(2, '0')}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setRestSeconds(s => s + 30); setRestRemaining(r => { if (restEndTimeRef.current) restEndTimeRef.current += 30000; return r + 30 }); }}
                          className="text-gray-400 hover:text-white text-sm px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
                        >＋30</button>
                      </>
                    )}
                    {restDone && <span className="text-xl font-bold text-black">00:00</span>}
                  </div>
                  <button type="button" onClick={stopRestTimer} className={`${restDone ? 'text-black' : 'text-gray-500 hover:text-white'} text-lg font-bold px-1`}>✕</button>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      <section className="mb-8">
        {(() => {
          // Consolidate all sets from all logs for this date (handles legacy multiple logs + new single log)
          const allSets = dailyLogs.flatMap(log => log.workout_sets || []).sort((a, b) => b.id - a.id)

          return (
            <>
              <h2 className="text-xl font-bold mb-4">{t('logs_for_date')} ({allSets.length})</h2>
              <div className="space-y-2">
                {allSets.length === 0 ? (
                  <div className="text-gray-500 text-center py-4">{t('no_logs_yet')}</div>
                ) : (
                  allSets.map((set, index) => {
                    // We need the log ID to delete. but sets are nested.
                    // If we want to delete a SET, we need a destroy endpoint for sets, OR delete the log if it's the last set?
                    // The current backend `destroy` deletes the whole LOG.
                    // With "One Log Per Day", we probably want to delete SETS individually.
                    // BUT, for now, let's keep it simple:
                    // We display sets. If the user clicks delete, we might have issues if we don't have set IDs or set-delete endpoint.
                    // Wait, existing `handleDelete` takes `logId`.
                    // If we switch to "One Log", deleting the log deletes ALL sets for the day.
                    // That might remain true for "Delete Daily Log" action.
                    // BUT the UI previously showed "Log cards" which were effectively sets.
                    // Now we show "Set cards".
                    // If I want to delete just ONE set, I need `api/v1/workout_sets/:id`.
                    // Existing `workout_logs_controller` has nested attributes support including `_destroy`.
                    // So I can update the log with `_destroy: true` for a specific set.
                    // This is getting complex for a quick refactor.
                    // ALTERNATIVE:
                    // Keep the display as is? NO, the display iterated over LOGS.
                    // Now we have ONE log with MANY sets.
                    // We must iterate over SETS.
                    // And we need a way to delete SETS.
                    // Let's implement `handleDeleteSet(logId, setId)`.

                    const exercise = set.exercise
                    const exerciseName = exercise ? ((i18n.language === 'en' && exercise.en_name) ? exercise.en_name : (exercise.name || exercise.en_name)) : 'Unknown'
                    const bodyPart = exercise?.body_part || ''

                    // Identify which log this set belongs to (usually dailyLogs[0])
                    // In flatMap, we lose the parent log ref unless we map differently.
                    const parentLog = dailyLogs.find(l => l.workout_sets?.some(s => s.id === set.id))

                    return (
                      <div key={set.id || index} className="bg-[#1c1c1e] px-5 py-2.5 rounded-xl border border-gray-800 shadow relative group">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <DumbbellIcon className="w-5 h-5 text-orange-500" />
                            <h3 className="font-bold text-lg">
                              <span className="text-gray-400 text-sm mr-2">{getBodyPartLabel(bodyPart)}</span>
                              {exerciseName}
                            </h3>
                          </div>
                          {/* Delete button: We need to delete the SET, not the whole log (unless it's the last set) */}
                          <button
                            onClick={async () => {
                              if (!confirm(t('confirm_delete'))) return
                              // Use update to destroy set
                              if (!parentLog) return

                              const payload = {
                                workout_log: {
                                  id: parentLog.id,
                                  date: parentLog.date, // needed for find_or_initialize logic if we used that, but we have ID.
                                  // Actually create uses find_by date. 
                                  // If we want to delete a set, we can use the same endpoint?
                                  // Backend `create` logic handles update.
                                  workout_sets_attributes: [{ id: set.id, _destroy: 1 }]
                                }
                              }

                              try {
                                const res = await fetch('/api/v1/workout_logs', {
                                  method: 'POST',
                                  // Strictly speaking, we are updating. `create` logic handles update.
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                                  },
                                  body: JSON.stringify(payload)
                                })
                                if (res.ok) {
                                  const updatedLog = await res.json()
                                  setLogs(prevLogs => {
                                    // Remove the set from UI or update the log
                                    // Since we get the updated log back, we just replace it.
                                    // Note: If all sets are gone, the log might still exist (with note).
                                    const existingIndex = prevLogs.findIndex(l => l.id === updatedLog.id)
                                    if (existingIndex >= 0) {
                                      const updated = [...prevLogs]
                                      updated[existingIndex] = updatedLog
                                      return updated
                                    }
                                    return prevLogs
                                  })
                                }
                              } catch (e) { console.error(e) }
                            }}
                            className="text-gray-600 hover:text-red-500 transition-colors"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-300 mt-2">
                          {bodyPart === '有酸素運動' ? (
                            <>
                              {set.time && (
                                <div>
                                  <span className="text-gray-500 mr-2">時間:</span>
                                  <span className="text-orange-500 font-bold">
                                    {Math.floor(set.time)}{t('unit_min')}
                                    {Math.round((set.time - Math.floor(set.time)) * 60) > 0 ? ` ${Math.round((set.time - Math.floor(set.time)) * 60)}${t('unit_sec')}` : ''}
                                  </span>
                                </div>
                              )}
                            </>
                          ) : (exercise?.name === 'プランク' || exercise?.en_name === 'Plank') ? (
                            <>
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">重量:</span>
                                <span className="text-orange-500 font-bold">{set.is_bodyweight ? t('bodyweight') : `${set.weight}kg`}</span>
                              </div>
                              {set.time != null && (
                                <div>
                                  <span className="text-gray-500 mr-2">時間:</span>
                                  <span className="text-orange-500 font-bold">
                                    {Math.floor(set.time)}{t('unit_min')}
                                    {Math.round((set.time - Math.floor(set.time)) * 60) > 0 ? ` ${Math.round((set.time - Math.floor(set.time)) * 60)}${t('unit_sec')}` : ''}
                                  </span>
                                </div>
                              )}
                            </>
                          ) : null}
                          {bodyPart === '有酸素運動' ? (
                            <>
                              {set.distance != null && (
                                <div>
                                  <span className="text-gray-500 mr-2">距離:</span>
                                  <span className="text-white font-bold">{set.distance}km</span>
                                </div>
                              )}
                              {set.floors != null && (
                                <div>
                                  <span className="text-gray-500 mr-2">{t('floors')}:</span>
                                  <span className="text-white font-bold">{set.floors}{t('unit_floors')}</span>
                                </div>
                              )}
                              {set.calories != null && (
                                <div>
                                  <span className="text-gray-500 mr-2">kcal:</span>
                                  <span className="text-white font-bold">{set.calories}</span>
                                </div>
                              )}
                              {set.route_data && set.route_data.length > 0 && (
                                <div className="w-full">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedRoute(set.route_data);
                                      setIsMapModalOpen(true);
                                    }}
                                    className="text-rose-500 hover:text-rose-400 text-xs font-bold flex items-center gap-1 border border-rose-500/30 px-2 py-1 rounded mt-2 bg-rose-500/5 hover:bg-rose-500/10 transition-colors"
                                  >
                                    <MapIcon className="w-3.5 h-3.5" />
                                    {t('tracking.view_map')}
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (exercise?.name !== 'プランク' && exercise?.en_name !== 'Plank') ? (
                            <>
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">重量:</span>
                                <span className="text-orange-500 font-bold">{set.is_bodyweight ? t('bodyweight') : `${set.weight}kg`}</span>
                                {progressivePRs[set.id] && (
                                  <span className="ml-2 bg-green-500/20 text-yellow-500 border border-green-500/50 text-[10px] uppercase font-bold px-2 py-0.5 rounded shadow-[0_0_8px_rgba(34,197,94,0.3)] flex items-center gap-1">
                                    <span className="text-[10px]">🏆</span> PR
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">回数:</span>
                                <span className="font-bold">{set.reps}{t('unit_reps')}</span>
                              </div>
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-2">セット:</span>
                                <span className="font-bold">{set.sets || 1}{t('unit_sets')}</span>
                              </div>
                            </>
                          ) : null}
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          {/* new Date(parentLog.date)... but parentLog might be missing in edge case */}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </>
          )
        })()}
      </section>

      {isSessionFinishableDate && hasWorkoutSets && (
        !todayLog?.session_ended_at && !todayLog?.ai_one_point ? (
          <section className="mb-8">
            <button
              onClick={handleFinishSession}
              disabled={isFinishing}
              className={`w-full font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 text-lg shadow-lg border-b-4 active:border-b-0 active:translate-y-1 ${isFinishing
                ? 'bg-gray-600 text-gray-400 border-gray-700 cursor-not-allowed'
                : 'bg-emerald-500 hover:bg-emerald-600 text-black border-emerald-700'
                }`}
            >
              <CheckCircle className="w-5 h-5" />
              {t('session.finish_button')}
            </button>
            <p className="mt-2 text-center text-xs text-emerald-400/80">
              {t('session.finish_hint')}
            </p>
          </section>
        ) : (
          <section className="mb-8">
            <div className="bg-[#1c1c1e] p-6 rounded-xl border border-orange-500/40 shadow-lg">
              <h2 className="text-lg font-bold text-white mb-4">🎉 {t('session.summary_title')}</h2>
              <div className="flex gap-3 mb-4 text-center">
                {sessionDurationMinutes && (
                  <div className="flex-1 bg-[#2c2c2e] rounded-lg p-3">
                    <div className="text-xl font-bold text-orange-400">{sessionDurationMinutes}<span className="text-xs ml-0.5">{t('session.minutes')}</span></div>
                    <div className="text-xs text-gray-400 mt-1">{t('session.duration')}</div>
                  </div>
                )}
                <div className="flex-1 bg-[#2c2c2e] rounded-lg p-3">
                  <div className="text-xl font-bold text-orange-400">{sessionExerciseCount}</div>
                  <div className="text-xs text-gray-400 mt-1">{t('session.exercises')}</div>
                </div>
                {sessionVolume > 0 && (
                  <div className="flex-1 bg-[#2c2c2e] rounded-lg p-3">
                    <div className="text-xl font-bold text-orange-400">{sessionVolume.toLocaleString()}<span className="text-xs ml-0.5">kg</span></div>
                    <div className="text-xs text-gray-400 mt-1">{t('session.volume')}</div>
                  </div>
                )}
              </div>

              {todayLog?.ai_one_point ? (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 text-gray-100 whitespace-pre-wrap leading-relaxed mb-4 animate-fade-in">
                  {stripMarkdown(todayLog.ai_one_point)}
                </div>
              ) : (
                <div className="text-sm text-gray-400 animate-pulse mb-4">{t('session.one_point_loading')}</div>
              )}

              <button
                onClick={() => document.getElementById('daily-note-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-4 h-4 shrink-0" />
                {t('session.ask_coach_button')}
              </button>
              <button
                onClick={() => {
                  setShareModalType('workout')
                  setIsShareModalOpen(true)
                }}
                className="w-full border border-gray-600 hover:border-gray-500 hover:bg-[#2c2c2e] text-gray-300 font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-3"
              >
                <Share2 className="w-4 h-4" />
                {t('share_workout')}
              </button>
            </div>
          </section>
        )
      )}

      <LockedFeatureOverlay locked={false} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('daily_note_label')}>
        <section id="daily-note-section" className="mb-8">
          <h2 className="text-xl font-bold mb-4">{t('daily_note_label')}</h2>
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-44 mb-4"
              placeholder={t('daily_note_placeholder')}
            />
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await fetch('/api/v1/workout_logs', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    },
                    body: JSON.stringify({ workout_log: { date, note } })
                  })
                  if (res.ok) {
                    const newLog = await res.json()
                    setLogs(prevLogs => {
                      const existingIndex = prevLogs.findIndex(l => l.id === newLog.id)
                      if (existingIndex >= 0) {
                        const updated = [...prevLogs]
                        updated[existingIndex] = newLog
                        return updated
                      }
                      return [newLog, ...prevLogs]
                    })
                    // Guide directly to the AI coach: the saved note is the user's message to the coach
                    highlightAiCoachButton()
                  }
                } catch (e) { console.error(e) }
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <span className="text-lg">{t('save_note')}</span>
            </button>
          </div>
        </section>
      </LockedFeatureOverlay>

      {/* Monthly Badge Section */}
      <section id="monthly-badge-section" className="mb-6 bg-[#1c1c1e] rounded-2xl p-5 border border-gray-800">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-bold text-white">{t('badge.monthly_title')}</h2>
          <span className="text-sm text-gray-400">{t('badge.sessions_count', { count: monthlySessions })}</span>
        </div>
        <div className="flex justify-around mb-4">
          {['bronze', 'silver', 'gold'].map(level => {
            const achieved = (monthlyBadge === level) ||
              (level === 'bronze' && ['silver', 'gold'].includes(monthlyBadge)) ||
              (level === 'silver' && monthlyBadge === 'gold')
            const sessions = monthlySessions
            const threshold = badgeThreshold(level)
            const isNext = !achieved && sessions < threshold &&
              (level === 'bronze' || (level === 'silver' && sessions >= 4) || (level === 'gold' && sessions >= 8))
            return (
              <div key={level} className="flex flex-col items-center gap-1">
                <img
                  src={`/badges/badge_${monthStr}_${level}.png`}
                  alt={badgeLabel(level)}
                  className={`w-16 h-16 transition-all rounded-xl ${achieved ? 'cursor-pointer active:scale-95' : 'opacity-30'}`}
                  style={{ backgroundColor: '#1c1c1e' }}
                  onClick={achieved ? () => { setEarnedBadge(level); setShowBadgeModal(true) } : undefined}
                  onError={e => { e.target.style.display = 'none' }}
                />
                <span className={`text-xs font-bold ${achieved ? badgeColor(level) : 'text-gray-600'}`}>
                  {badgeLabel(level)}
                </span>
                {isNext && (
                  <span className="text-xs text-gray-500">{t('badge.remaining', { count: threshold - sessions })}</span>
                )}
              </div>
            )
          })}
        </div>
        {/* 総評ボタン */}
        {monthlyBadge !== 'none' && (
          <div className="mt-2">
            {currentUser?.can_use_monthly_summary ? (
              <div>
                <button
                  onClick={() => showSummary ? setShowSummary(false) : fetchMonthlySummary(monthlyBadge)}
                  disabled={summaryLoading}
                  className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors disabled:opacity-50"
                >
                  {summaryLoading ? t('badge.generating') : showSummary ? t('badge.close_summary') : t('badge.view_summary')}
                </button>
                {showSummary && monthlySummary && (
                  <div className="mt-3 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap bg-[#2c2c2e] rounded-xl p-4">
                    {stripMarkdown(monthlySummary)}
                  </div>
                )}
                {showSummary && !monthlySummary && !summaryLoading && (
                  <p className="mt-2 text-xs text-gray-500 text-center">{t('badge.summary_pending')}</p>
                )}
              </div>
            ) : (
              <button
                onClick={() => setIsUpgradeModalOpen(true)}
                className="w-full py-2 rounded-lg bg-gray-700 text-gray-500 text-sm font-bold cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span>🔒</span> {t('badge.view_summary_premium')}
              </button>
            )}
          </div>
        )}
        <div className="flex justify-end mt-3">
          <button
            onClick={() => onNavigate('past_badges')}
            className="text-xs text-gray-500 hover:text-orange-400 transition-colors underline underline-offset-2"
          >
            {t('badge.past_badges')}
          </button>
        </div>
      </section>

      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{t('workout_calendar')}</h2>
          <button
            onClick={() => {
              setShareModalType('workout')
              setIsShareModalOpen(true)
            }}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm"
          >
            <Share2 className="w-4 h-4" />
            {t('share_workout')}
          </button>
        </div>
        <Calendar
          logs={logs}
          selectedDate={date}
          onDateSelect={setDate}
        />
        <button
          onClick={() => onNavigate('reflections')}
          className="w-full mt-6 bg-green-600 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-green-700 transition-all flex items-center justify-center gap-3 border-b-4 border-green-800 active:border-b-0 active:translate-y-1 group"
        >
          <MessageSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
          <span className="text-lg">{t('past_reflections_button')}</span>
        </button>
      </section>

      <LockedFeatureOverlay locked={isFreeMember} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('balance')}>
        <section className="mb-8 font-inter">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{t('balance')} ({new Date(date).toLocaleDateString()})</h2>
            <button
              onClick={() => {
                setShareModalType('balance')
                setIsShareModalOpen(true)
              }}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm"
            >
              <Share2 className="w-4 h-4" />
              {t('share_balance')}
            </button>
          </div>
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
            <BodyPartRadarChart logs={dailyLogs} />
          </div>
        </section>
      </LockedFeatureOverlay>

      <LockedFeatureOverlay locked={isFreeMember} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('daily_volume')}>
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">{t('daily_volume')} ({new Date(date).toLocaleDateString()})</h2>
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
            <DailyVolumeChart data={(() => {
              const volumeByPart = {}
              dailyLogs.forEach(log => {
                if (log.workout_sets) {
                  log.workout_sets.forEach(set => {
                    const part = set.exercise ? set.exercise.body_part : 'Unknown'
                    if (part === '有酸素運動') return 

                    const setSets = set.sets || 1
                    const effectiveWeight = set.is_bodyweight
                      ? (parseFloat(currentUser?.weight) || 60)
                      : set.weight
                    const isPlankSet = set.exercise?.name === 'プランク' || set.exercise?.en_name === 'Plank'
                    if (effectiveWeight && (set.reps || (isPlankSet && set.time))) {
                      const volume = isPlankSet
                        ? effectiveWeight * set.time * setSets
                        : effectiveWeight * set.reps * setSets
                      if (!volumeByPart[part]) volumeByPart[part] = 0
                      volumeByPart[part] += volume
                    }
                  })
                }
              })

              return Object.keys(volumeByPart).map(part => ({
                name: getBodyPartLabel(part),
                volume: volumeByPart[part]
              }))
            })()} />
          </div>
        </section>
      </LockedFeatureOverlay>

      <LockedFeatureOverlay locked={isFreeMember} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('period_comparison')}>
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{t('period_comparison')} ({comparisonPeriod === 'week' ? t('week') : t('month')})</h2>
            <div className="flex bg-[#2c2c2e] rounded-lg p-1">
              <button
                onClick={() => setComparisonPeriod('week')}
                className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${comparisonPeriod === 'week'
                  ? 'bg-orange-500 text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('week')}
              </button>
              <button
                onClick={() => setComparisonPeriod('month')}
                className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${comparisonPeriod === 'month'
                  ? 'bg-orange-500 text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('month')}
              </button>
            </div>
          </div>
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
            <PeriodComparisonChart logs={logs} period={comparisonPeriod} />
          </div>
        </section>
      </LockedFeatureOverlay>

      <LockedFeatureOverlay locked={isFreeMember} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('three_d_model_title', { period: humanBodyPeriod === 'week' ? t('week') : t('month') })}>
        <section className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{t('three_d_model_title', { period: humanBodyPeriod === 'week' ? t('week') : t('month') })}</h2>
            <div className="flex bg-[#2c2c2e] rounded-lg p-1">
              <button
                onClick={() => setHumanBodyPeriod('week')}
                className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${humanBodyPeriod === 'week'
                  ? 'bg-orange-500 text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('week')}
              </button>
              <button
                onClick={() => setHumanBodyPeriod('month')}
                className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${humanBodyPeriod === 'month'
                  ? 'bg-orange-500 text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('month')}
              </button>
            </div>
          </div>
          <HumanBodyDiagram volumeData={humanBodyVolumeData} />
        </section>
      </LockedFeatureOverlay>

      <LockedFeatureOverlay locked={false} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('training_goal')}>
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">{t('training_goal')}</h2>
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
            {currentUser?.training_goal ? (
              <p className="text-gray-300 whitespace-pre-wrap">{currentUser.training_goal}</p>
            ) : (
              <div className="text-center">
                <p className="text-gray-500 mb-4">{t('goal_not_set')}</p>
                <a
                  href={`/users/edit?locale=${i18n.language}`}
                  className="inline-block bg-[#2c2c2e] hover:bg-[#3a3a3c] text-white border border-gray-700 font-bold py-2 px-6 rounded-lg transition-colors text-sm"
                >
                  {t('set_goal_link')}
                </a>
              </div>
            )}
          </div>
        </section>
      </LockedFeatureOverlay>

      <LockedFeatureOverlay locked={false} onLockClick={() => setIsUpgradeModalOpen(true)} title={t('ai_coach.title')}>
        {/* AI Coach Section */}
        <section id="ai-coach-section" className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold mb-2">{t('ai_coach.title')}</h2>
            <div className="border border-blue-500 text-blue-400 p-3 rounded-lg text-sm mb-4">
              {t('ai_coach.data_usage_notice')}
            </div>
          </div>

          {hasWorkoutSets ? (
            <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
                {[
                  { id: 'supportive', label: t('coach_types.supportive') },
                  { id: 'strict', label: t('coach_types.strict') },
                  { id: 'technical', label: t('coach_types.technical') },
                  { id: 'funny', label: t('coach_types.funny') }
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setCoachType(type.id)}
                    className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${coachType === type.id
                      ? 'bg-orange-500 text-black'
                      : 'bg-[#2c2c2e] text-gray-400 hover:text-white'
                      }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              <button
                id="ai-coach-button"
                onClick={handleAskAiCoach}
                disabled={isLoadingAdvice || !note.trim()}
                className={`w-full font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${(isLoadingAdvice || !note.trim())
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
              >
                {isLoadingAdvice ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('ai_coach.generating')}
                  </>
                ) : (
                  <>
                    <span className="text-lg">{t('ai_coach.ask_button')}</span>
                  </>
                )}
              </button>

              {!note.trim() && (
                <div className="text-center text-xs text-gray-400 mt-2">
                  {t('ai_coach.note_required')}
                </div>
              )}

              {usageSummary && (
                <div className="text-center text-xs text-gray-500 mt-2">
                  {usageSummary.ai_coach.unlimited ? (
                    usageSummary.membership_status === 'trial'
                      ? <>トライアル中: プレミアム機能を体験中（無制限）</>
                      : <>今月: 無制限</>
                  ) : (
                    <>今月あと <span className="text-orange-400 font-bold">{usageSummary.ai_coach.remaining}</span> / {usageSummary.ai_coach.limit} 回</>
                  )}
                </div>
              )}

              {aiAdvice && !isLoadingAdvice && (
                <div className="mt-6 border-t border-gray-700 pt-6">
                  <div className="bg-[#2c2c2e] p-4 rounded-lg text-gray-200 whitespace-pre-wrap leading-relaxed animate-fade-in border border-gray-700">
                    {stripMarkdown(aiAdvice)}
                  </div>

                  {coachMessages.map(m => (
                    <div
                      key={m.id}
                      className={m.role === 'user'
                        ? 'mt-3 ml-8 bg-orange-500/10 border border-orange-500/40 p-3 rounded-lg text-gray-200 text-sm whitespace-pre-wrap'
                        : 'mt-3 mr-8 bg-[#2c2c2e] border border-gray-700 p-3 rounded-lg text-gray-200 text-sm whitespace-pre-wrap leading-relaxed'}
                    >
                      {m.role === 'user' ? m.content : stripMarkdown(m.content)}
                    </div>
                  ))}

                  {isLoadingAnswer && (
                    <div className="mt-3 text-sm text-gray-400 animate-pulse">{t('ai_coach.answering')}</div>
                  )}

                  {coachMessages.filter(m => m.role === 'user').length < 3 ? (
                    <div className="mt-4">
                      <textarea
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        maxLength={200}
                        placeholder={t('ai_coach.question_placeholder')}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-20"
                      />
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {questionText.length}/200・{t('ai_coach.questions_remaining', { count: 3 - coachMessages.filter(m => m.role === 'user').length })}
                        </span>
                        <button
                          onClick={handleAskQuestion}
                          disabled={isLoadingAnswer || !questionText.trim()}
                          className={`font-bold px-6 py-2 rounded-lg transition-colors text-sm ${(isLoadingAnswer || !questionText.trim())
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}
                        >
                          {t('ai_coach.ask_question')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 text-center text-xs text-gray-500">{t('ai_coach.question_limit_reached')}</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">
              <p>{t('ai_coach.no_logs')}</p>
            </div>
          )}
        </section>
      </LockedFeatureOverlay>

      {isImportModalOpen && (
        <ImportWorkout
          exercises={exercises}
          onClose={() => setIsImportModalOpen(false)}
          onImportComplete={() => {
            fetchInitialData() // refresh logs and exercises
            setIsImportModalOpen(false)
            alert(t('success_added'))
          }}
        />
      )}

      <React.Suspense fallback={null}>
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          logs={dailyLogs}
          date={date}
          note={note}
          type={shareModalType}
          progressivePRs={progressivePRs}
        />
      </React.Suspense>

      <PremiumUpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />

      {/* Badge Achievement Modal - PullToRefreshのtransform外にportalで配置 */}
      {showBadgeModal && earnedBadge && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => { setShowBadgeModal(false); setModalSummaryMode(false) }}
        >
          <div
            className="bg-[#1c1c1e] rounded-3xl p-8 mx-6 text-center border border-gray-700 shadow-2xl max-w-sm w-full max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {modalSummaryMode ? (
              <p className="text-gray-400 text-sm py-12">{t('badge.generating')}</p>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-2">{t('badge.congrats')}</p>
                <img
                  src={`/badges/badge_${monthStr}_${earnedBadge}.png`}
                  alt={badgeLabel(earnedBadge)}
                  className="w-64 h-64 mx-auto my-4 rounded-xl"
                  style={{ backgroundColor: '#1c1c1e' }}
                  onError={e => { e.target.style.display = 'none' }}
                />
                <h3 className={`text-2xl font-bold mb-1 ${badgeColor(earnedBadge)}`}>
                  {t('badge.earned', { label: badgeLabel(earnedBadge) })}
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  {t('badge.achieved_times', { count: badgeThreshold(earnedBadge) })}
                </p>
                {currentUser?.can_use_monthly_summary ? (
                  <button
                    onClick={() => { setModalSummaryMode(true); fetchMonthlySummary(earnedBadge) }}
                    className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors mb-3"
                  >
                    {t('badge.view_summary')}
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowBadgeModal(false); setModalSummaryMode(false); setIsUpgradeModalOpen(true) }}
                    className="w-full py-3 rounded-xl bg-gray-700 text-gray-500 font-bold text-sm cursor-not-allowed mb-3 flex items-center justify-center gap-2"
                  >
                    <span>🔒</span> {t('badge.view_summary_premium')}
                  </button>
                )}
                <button
                  onClick={() => { setShowBadgeModal(false); setModalSummaryMode(false) }}
                  className="w-full py-2 rounded-xl text-gray-500 text-sm hover:text-gray-300 transition-colors"
                >
                  {t('badge.view_later')}
                </button>
              </>
            )}
          </div>
        </div>
      , document.body)}
      <Footer showLinks={false} showSocial={false} />
      {isMapModalOpen && (
        <RouteViewer
          path={selectedRoute}
          onClose={() => setIsMapModalOpen(false)}
        />
      )}

    </div>
  )
}

export default Dashboard
